class VideoModal extends HTMLElement {
  constructor() {
    super();
    this.modal = this.querySelector('[data-video-modal]');
    this.iframe = this.querySelector('[data-video-iframe]');
    this.videoElement = this.querySelector('[data-video-element]');
    this.overlay = this.querySelector('[data-video-overlay]');
    this.closeButton = this.querySelector('[data-close-modal]');
    this.triggerElement = null;

    // Bind event handlers
    this.handleEscapeKey = this.handleEscapeKey.bind(this);
    this.handleOverlayClick = this.handleOverlayClick.bind(this);
    this.handleFocusTrap = this.handleFocusTrap.bind(this);
  }

  connectedCallback() {
    // Overlay click event
    if (this.overlay) {
      this.overlay.addEventListener('click', this.handleOverlayClick);
    }

    // Close button click event
    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => this.close());
    }

    // Make globally accessible
    window.videoModal = this;
  }

  open(videoUrl, triggerElement = null) {
    if (!videoUrl) return;

    // Store trigger element for focus restoration
    this.triggerElement = triggerElement || document.activeElement;

    // Set iframe title from the trigger's aria-label for accessibility
    const videoTitle = this.triggerElement
      ? this.triggerElement.getAttribute('aria-label') || 'Video player'
      : 'Video player';
    this.iframe.setAttribute('title', videoTitle);

    // Determine video type and prepare accordingly
    const videoType = this.getVideoType(videoUrl);

    if (videoType === 'youtube' || videoType === 'vimeo') {
      // Use iframe for YouTube/Vimeo
      const embedUrl = this.convertToEmbedUrl(videoUrl);
      this.iframe.src = embedUrl;
      this.iframe.classList.remove('hidden');
      this.videoElement.classList.add('hidden');
      this.videoElement.pause();
      this.videoElement.src = '';
    } else {
      // Use video element for Shopify/direct videos
      this.videoElement.src = videoUrl;
      this.videoElement.classList.remove('hidden');
      this.iframe.classList.add('hidden');
      this.iframe.src = '';
    }

    // Show modal
    this.modal.classList.remove('hidden');
    this.modal.classList.add('flex');
    setTimeout(() => {
      this.modal.classList.remove('opacity-0');
      // Focus close button after modal is visible
      if (this.closeButton) {
        this.closeButton.focus();
      }
    }, 10);

    // Disable body scroll while preserving scroll position
    this.scrollPosition = window.scrollY;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.scrollPosition}px`;
    document.body.style.width = '100%';

    // Add keyboard listeners
    document.addEventListener('keydown', this.handleEscapeKey);
    document.addEventListener('keydown', this.handleFocusTrap);
  }

  close() {
    // Hide modal
    this.modal.classList.add('opacity-0');
    setTimeout(() => {
      this.modal.classList.remove('flex');
      this.modal.classList.add('hidden');
      // Clear sources after animation
      this.iframe.src = '';
      this.videoElement.pause();
      this.videoElement.src = '';
    }, 300);

    // Enable body scroll and restore scroll position
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, this.scrollPosition);

    // Return focus to trigger element
    if (this.triggerElement) {
      this.triggerElement.focus();
      this.triggerElement = null;
    }

    // Remove keyboard listeners
    document.removeEventListener('keydown', this.handleEscapeKey);
    document.removeEventListener('keydown', this.handleFocusTrap);
  }

  getVideoType(url) {
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      return 'youtube';
    } else if (url.includes('vimeo.com')) {
      return 'vimeo';
    } else {
      // Assume it's a direct video file (Shopify or other)
      return 'direct';
    }
  }

  handleEscapeKey(event) {
    if (event.key === 'Escape') {
      this.close();
    }
  }

  handleOverlayClick(event) {
    // Close if clicking the overlay itself, not the content
    if (event.target === this.overlay) {
      this.close();
    }
  }

  handleFocusTrap(event) {
    if (event.key !== 'Tab') return;

    // Get all focusable elements within modal
    const focusableElements = this.modal.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"]), iframe, video'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    // Trap focus within modal
    if (event.shiftKey) {
      // Shift + Tab: if on first element, move to last
      if (document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      }
    } else {
      // Tab: if on last element, move to first
      if (document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }
  }

  convertToEmbedUrl(url) {
    let embedUrl = '';

    // YouTube
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      let videoId = '';
      if (url.includes('youtu.be/')) {
        videoId = url.split('youtu.be/')[1].split('?')[0].split('&')[0];
      } else if (url.includes('youtube.com')) {
        const urlParams = new URLSearchParams(new URL(url).search);
        videoId = urlParams.get('v');
      }
      embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;
    }
    // Vimeo
    else if (url.includes('vimeo.com')) {
      const videoId = url.split('vimeo.com/')[1].split('?')[0].split('/')[0];
      embedUrl = `https://player.vimeo.com/video/${videoId}?autoplay=1`;
    }
    // Already an embed URL or other video source
    else {
      embedUrl = url;
    }

    return embedUrl;
  }
}

customElements.define('video-modal', VideoModal);

// Global helper function for opening video modal
window.openVideoModal = function(videoUrl) {
  if (window.videoModal) {
    window.videoModal.open(videoUrl);
  }
};
